import type { LucideIcon } from 'lucide-react';
import { Info, Sparkles, User } from 'lucide-react';
import type { VisualizationData } from '../../../nebula/types';
import type { NebulaMessage } from '../../../types/nebula-insights';

const VIZ_CHART_TYPES = new Set([
  'bar_chart',
  'line_chart',
  'pie_chart',
  'table',
  'summary_card',
]);

function isVisualizationData(value: unknown): value is VisualizationData {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  const config = obj.config;
  if (!config || typeof config !== 'object') return false;
  return (
    typeof (config as Record<string, unknown>).type === 'string' &&
    Array.isArray(obj.data)
  );
}

function isVisualizationJsonPayload(value: unknown): boolean {
  if (isVisualizationData(value)) return true;
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.type === 'string' &&
    VIZ_CHART_TYPES.has(record.type) &&
    Array.isArray(record.data)
  );
}

export function getMessageVisualizations(message: NebulaMessage): VisualizationData[] {
  const visualizations: VisualizationData[] = [];

  if (isVisualizationData(message.metadata?.visualization)) {
    visualizations.push(message.metadata.visualization);
  }

  if (Array.isArray(message.content)) {
    for (const block of message.content) {
      if (!block || typeof block !== 'object') continue;
      const obj = block as Record<string, unknown>;
      if (obj.type === 'visualization' && isVisualizationData(obj.data)) {
        visualizations.push(obj.data);
      }
    }
  }

  return visualizations;
}

function tryExtractJsonObject(text: string, startIndex: number): { value: unknown; end: number } | null {
  if (text[startIndex] !== '{') return null;

  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = startIndex; i < text.length; i += 1) {
    const ch = text[i];

    if (inString) {
      if (escape) escape = false;
      else if (ch === '\\') escape = true;
      else if (ch === '"') inString = false;
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        const candidate = text.slice(startIndex, i + 1);
        try {
          return { value: JSON.parse(candidate), end: i + 1 };
        } catch {
          return null;
        }
      }
    }
  }

  return null;
}

export function isHiddenCodeBlockContent(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return true;
  try {
    return isVisualizationJsonPayload(JSON.parse(trimmed));
  } catch {
    return false;
  }
}

function stripEmptyMarkdownCodeBlocks(text: string): string {
  return text
    .replace(/```[a-zA-Z]*\s*```/g, '')
    .replace(/^```[a-zA-Z]*\s*$/gm, '')
    .replace(/^(?: {4}|\t)\s*$/gm, '');
}

/** Remove visualization JSON payloads embedded in message text (fenced or inline). */
export function stripVisualizationJsonFromText(text: string): string {
  let result = text.replace(/```[a-zA-Z]*\s*\n?([\s\S]*?)```/gi, (match, inner: string) => {
    const trimmedInner = inner.trim();
    if (!trimmedInner) return '';
    try {
      const parsed = JSON.parse(trimmedInner);
      if (isVisualizationJsonPayload(parsed)) return '';
    } catch {
      // Keep non-JSON code blocks.
    }
    return match;
  });

  let index = 0;
  while (index < result.length) {
    const braceIndex = result.indexOf('{', index);
    if (braceIndex === -1) break;

    const extracted = tryExtractJsonObject(result, braceIndex);
    if (extracted && isVisualizationJsonPayload(extracted.value)) {
      result = `${result.slice(0, braceIndex)}${result.slice(extracted.end)}`;
      index = braceIndex;
      continue;
    }

    index = braceIndex + 1;
  }

  result = stripEmptyMarkdownCodeBlocks(result);
  return result.replace(/\n{3,}/g, '\n\n').trim();
}

/** Strip AI wrapper tags like <answer>, </answer> from stored message text. */
export function stripXmlWrapperTags(text: string): string {
  return text
    .replace(/<\/?[a-zA-Z][a-zA-Z0-9_-]*(?:\s[^>]*)?\/?>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Extract display text from Nebula message content (string or structured JSONB).
 */
export function formatMessageContent(content: unknown): string {
  if (content == null) {
    return '';
  }

  if (typeof content === 'string') {
    const trimmed = content.replace(/^"|"$/g, '').trim();
    try {
      const parsed = JSON.parse(trimmed);
      if (isVisualizationJsonPayload(parsed)) return '';
    } catch {
      // Plain text message.
    }
    return trimmed;
  }

  if (Array.isArray(content)) {
    return content
      .map((block) => {
        if (typeof block === 'string') return block;
        if (block && typeof block === 'object') {
          const obj = block as Record<string, unknown>;
          if (typeof obj.text === 'string') return obj.text;
          if (obj.type === 'text' && typeof obj.content === 'string') return obj.content;
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }

  if (typeof content === 'object') {
    const obj = content as Record<string, unknown>;
    if (isVisualizationJsonPayload(content)) return '';
    if (typeof obj.text === 'string') return obj.text;
    try {
      return JSON.stringify(content, null, 2);
    } catch {
      return String(content);
    }
  }

  return String(content);
}

export function formatMessageContentForDisplay(content: unknown): string {
  return stripVisualizationJsonFromText(stripXmlWrapperTags(formatMessageContent(content)));
}

export interface MessageRoleDisplay {
  label: string;
  icon: LucideIcon;
  cardClass: string;
  badgeClass: string;
}

export function getMessageRoleDisplay(role: NebulaMessage['role']): MessageRoleDisplay {
  switch (role) {
    case 'user':
      return {
        label: 'Artist question',
        icon: User,
        cardClass: 'border-l-gray-800 bg-gray-50',
        badgeClass: 'bg-gray-900 text-white',
      };
    case 'assistant':
      return {
        label: 'Nebula reply',
        icon: Sparkles,
        cardClass: 'border-l-blue-500 bg-blue-50/40',
        badgeClass: 'bg-blue-100 text-blue-800',
      };
    case 'system':
    default:
      return {
        label: 'System context',
        icon: Info,
        cardClass: 'border-l-amber-400 bg-amber-50/50',
        badgeClass: 'bg-amber-100 text-amber-900',
      };
  }
}

export function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatUserName(
  firstname?: string | null,
  lastname?: string | null,
  fallback = 'Unknown user'
): string {
  const name = [firstname, lastname].filter(Boolean).join(' ').trim();
  return name || fallback;
}

export const ALL_TIME_KPI_NOTE = 'All-time total · no period comparison';

export function formatRecentPeriodRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const startLabel = new Date(start).toLocaleDateString(undefined, opts);
  const endLabel = new Date(end).toLocaleDateString(undefined, opts);
  return `${startLabel} – ${endLabel}`;
}

export function formatChangePct(changePct: number | null | undefined): string | null {
  if (changePct == null || Number.isNaN(changePct)) return null;
  const prefix = changePct > 0 ? '+' : '';
  return `${prefix}${changePct}% vs prior period`;
}
